import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Cropper from 'cropperjs';
import { DialogIncludesTypes } from '@app/core/models/diallog';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import { FileToBase64 } from '@app/utils/converter';
import { BookingCustomerMainPhotoDialogData } from './booking-customer-main-photo-dialog.model';

const MAX_FILE_MB = 1;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const CROPPED_MAX_SIZE = 1200;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

@UntilDestroy()
@Component({
  selector: 'app-booking-customer-main-photo-dialog',
  templateUrl: './booking-customer-main-photo-dialog.component.html',
  styleUrls: ['./booking-customer-main-photo-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookingCustomerMainPhotoDialogComponent implements OnDestroy {
  @ViewChild('cropImage', { static: false }) cropImageRef: ElementRef<HTMLImageElement>;

  preview: string | null = null;
  selectedFile: File | null = null;
  loading = false;
  applyLoading = false;
  bookingCustomerId: string | null = null;
  showCropper = false;

  private cropper: Cropper | null = null;

  get isApplyEnabled(): boolean {
    if (this.applyLoading) {
      return false;
    }
    if (this.showCropper) {
      return !!this.cropper;
    }
    return !!this.selectedFile;
  }

  constructor(
    public ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private bookingService: BookingService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {
    const data = (this.config.data || {}) as BookingCustomerMainPhotoDialogData;
    this.preview = data.preview ?? null;
    this.bookingCustomerId = data.bookingCustomerId ?? null;
  }

  ngOnDestroy(): void {
    this.destroyCropper();
  }

  onFileChange(files: FileList, inputEl: HTMLInputElement): void {
    if (!files?.length || !this.bookingCustomerId) {
      return;
    }
    const file = files[0];
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: 'Допустимые форматы: PNG, JPG, JPEG.'
      });
      if (inputEl) {
        inputEl.value = '';
      }
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: `Размер файла не должен превышать ${MAX_FILE_MB} Мб.`
      });
      return;
    }
    this.destroyCropper();
    this.selectedFile = file;
    this.loading = false;
    this.showCropper = false;
    this.cdr.markForCheck();
    FileToBase64(file).then((dataUrl: unknown) => {
      this.preview = (dataUrl as string) || null;
      this.showCropper = true;
      this.cdr.markForCheck();
    });
    if (inputEl) {
      inputEl.value = '';
    }
  }

  onCropImageLoad(): void {
    if (!this.bookingCustomerId || !this.showCropper || !this.cropImageRef?.nativeElement) {
      return;
    }
    this.destroyCropper();
    requestAnimationFrame(() => {
      if (!this.cropImageRef?.nativeElement) {
        return;
      }
      this.cropper = new Cropper(this.cropImageRef.nativeElement, {
        viewMode: 1,
        dragMode: 'crop',
        movable: false,
        autoCropArea: 0.9,
        restore: false,
        guides: true,
        center: true,
        highlight: true,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false
      });
      this.cdr.markForCheck();
    });
  }

  private destroyCropper(): void {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
  }

  remove(): void {
    this.destroyCropper();
    this.preview = null;
    this.selectedFile = null;
    this.showCropper = false;
    this.cdr.markForCheck();
  }

  onApply(): void {
    const id = this.bookingCustomerId;
    if (!id) {
      return;
    }
    if (this.cropper) {
      this.applyLoading = true;
      this.cdr.markForCheck();
      const canvas = this.cropper.getCroppedCanvas({
        maxWidth: CROPPED_MAX_SIZE,
        maxHeight: CROPPED_MAX_SIZE,
        imageSmoothingQuality: 'high'
      });
      if (!canvas) {
        this.applyLoading = false;
        this.cdr.markForCheck();
        return;
      }
      canvas.toBlob(
        blob => {
          if (!blob) {
            this.applyLoading = false;
            this.cdr.markForCheck();
            return;
          }
          const name = this.selectedFile?.name || 'photo.jpg';
          const safeName = /\.(jpe?g|png)$/i.test(name) ? name : name.replace(/\.[^.]+$/i, '') + '.jpg';
          const file = new File([blob], safeName, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          this.bookingService.updateBookingCustomerMainPhoto(id, file).pipe(untilDestroyed(this)).subscribe(
            () => {
              this.applyLoading = false;
              this.ref.close({ applied: true });
              this.cdr.markForCheck();
            },
            err => {
              this.applyLoading = false;
              this.notificationService.onDialog({
                type: DialogIncludesTypes.PopupErrorTemplate,
                header: 'Ошибка',
                content: (err && (err.message || err.error)) || 'Не удалось обновить фото.'
              });
              this.cdr.markForCheck();
            }
          );
        },
        'image/jpeg',
        0.92
      );
      return;
    }
    if (!this.selectedFile) {
      return;
    }
    this.applyLoading = true;
    this.cdr.markForCheck();
    this.bookingService.updateBookingCustomerMainPhoto(id, this.selectedFile).pipe(untilDestroyed(this)).subscribe(
      () => {
        this.applyLoading = false;
        this.ref.close({ applied: true });
        this.cdr.markForCheck();
      },
      err => {
        this.applyLoading = false;
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка',
          content: (err && (err.message || err.error)) || 'Не удалось обновить фото.'
        });
        this.cdr.markForCheck();
      }
    );
  }

  onCancel(): void {
    this.ref.close();
  }
}
